/**
 * Templates Page
 *
 * Manage clinical note templates for conversation summaries.
 */

import { Header, Sidebar } from '@/components/layout';
import { TemplateScopeTabs } from '@/components/templates/TemplateScopeTabs';
import { isPersonalTemplate } from '@/components/templates/template-utils';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Textarea } from '@/components/ui/textarea';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { useTemplateDefaults } from '@/hooks/useTemplateDefaults';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Copy,
  Eye,
  FileText,
  Loader2,
  MessageSquare,
  Mic,
  MoreHorizontal,
  Pencil,
  Plus,
  Search,
  Trash2,
  X,
} from 'lucide-react';
import { useState } from 'react';

interface Template {
  id: number;
  name: string;
  description: string | null;
  template_content: string;
  example_note: string | null;
  custom_prompt: string | null;
  is_default: boolean;
  created_by_id: number | null;
  scope?: 'global' | 'org' | 'personal';
  is_personal?: boolean;
  is_org?: boolean;
  is_global?: boolean;
  is_role_based?: boolean;
  source_template_id?: number | null;
}

// Fetch templates
async function fetchTemplates(): Promise<Template[]> {
  const response = await fetch('/api/templates', {
    credentials: 'include',
  });
  if (!response.ok) {
    throw new Error('Failed to fetch templates');
  }
  return response.json();
}

// Fetch single template (includes full content)
async function fetchTemplate(id: number): Promise<Template> {
  const response = await fetch(`/api/templates/${id}`, {
    credentials: 'include',
  });
  if (!response.ok) {
    throw new Error('Failed to fetch template');
  }
  return response.json();
}

// Create template
async function createTemplate(data: Partial<Template>): Promise<Template> {
  const response = await fetch('/api/templates', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(data),
  });
  if (!response.ok) {
    throw new Error('Failed to create template');
  }
  return response.json();
}

// Update template
async function updateTemplate(id: number, data: Partial<Template>): Promise<Template> {
  const response = await fetch(`/api/templates/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(data),
  });
  if (!response.ok) {
    throw new Error('Failed to update template');
  }
  return response.json();
}

// Delete template
async function deleteTemplate(id: number): Promise<void> {
  const response = await fetch(`/api/templates/${id}`, {
    method: 'DELETE',
    credentials: 'include',
  });
  if (!response.ok) {
    throw new Error('Failed to delete template');
  }
}

async function copyOrgTemplate(id: number): Promise<Template> {
  const response = await fetch(`/api/templates/${id}/copy`, {
    method: 'POST',
    credentials: 'include',
  });
  if (!response.ok) {
    throw new Error('Failed to copy template');
  }
  return response.json();
}

interface CategorizedTemplates {
  inUse: Template[];
  myTemplates: Template[];
  orgTemplates: Template[];
}

export default function Templates() {
  const [searchQuery, setSearchQuery] = useState('');
  const [editingTemplate, setEditingTemplate] = useState<Template | null>(null);
  const [copyTemplate, setCopyTemplate] = useState<Template | null>(null);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<Template | null>(null);
  const [isLoadingTemplate, setIsLoadingTemplate] = useState(false);
  const [isLoadingCopyTemplate, setIsLoadingCopyTemplate] = useState(false);
  const [expandedSections, setExpandedSections] = useState<string[]>(['in-use', 'my-templates', 'org-templates']);

  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  // Personal templates now follow the person across orgs, but org templates are
  // org-specific — so the list differs per active org. Key the query by the
  // active org so switching orgs refetches instead of serving a stale cache.
  const activeOrgId = user?.authOrgId;

  // Default templates are persisted server-side (auth-scoped user profile).
  const {
    dictation: defaultDictationTemplateId,
    conversation: defaultConversationTemplateId,
    setTemplateDefault,
  } = useTemplateDefaults();

  const setDefaultDictation = (templateId: number | null) => {
    setTemplateDefault('dictation', templateId, {
      onSuccess: () =>
        toast({
          title: templateId
            ? 'Sjálfgefið sniðmát fyrir dikteringu stillt'
            : 'Sjálfgefið sniðmát fyrir dikteringu fjarlægt',
        }),
      onError: () =>
        toast({ title: 'Villa', description: 'Ekki tókst að vista sjálfgefið sniðmát', variant: 'destructive' }),
    });
  };

  const setDefaultConversation = (templateId: number | null) => {
    setTemplateDefault('conversation', templateId, {
      onSuccess: () =>
        toast({
          title: templateId ? 'Sjálfgefið sniðmát fyrir samtal stillt' : 'Sjálfgefið sniðmát fyrir samtal fjarlægt',
        }),
      onError: () =>
        toast({ title: 'Villa', description: 'Ekki tókst að vista sjálfgefið sniðmát', variant: 'destructive' }),
    });
  };

  // Handle template click - fetch full template data
  const handleTemplateClick = async (template: Template) => {
    setIsLoadingTemplate(true);
    try {
      const fullTemplate = await fetchTemplate(template.id);
      setEditingTemplate(fullTemplate);
    } catch {
      toast({ title: 'Villa', description: 'Ekki tókst að sækja sniðmát', variant: 'destructive' });
    } finally {
      setIsLoadingTemplate(false);
    }
  };

  const handleOrgTemplatePreview = async (template: Template) => {
    setIsLoadingCopyTemplate(true);
    try {
      const fullTemplate = await fetchTemplate(template.id);
      setCopyTemplate(fullTemplate);
    } catch {
      toast({ title: 'Villa', description: 'Ekki tókst að sækja sniðmát', variant: 'destructive' });
    } finally {
      setIsLoadingCopyTemplate(false);
    }
  };

  // Fetch templates
  const { data: templates = [], isLoading } = useQuery({
    queryKey: ['templates', activeOrgId],
    queryFn: fetchTemplates,
  });

  // Create mutation
  const createMutation = useMutation({
    mutationFn: createTemplate,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['templates'] });
      setIsCreateOpen(false);
      toast({ title: 'Sniðmát búið til' });
    },
    onError: () => {
      toast({ title: 'Villa', description: 'Ekki tókst að búa til sniðmát', variant: 'destructive' });
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<Template> }) => updateTemplate(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['templates'] });
      setEditingTemplate(null);
      toast({ title: 'Sniðmát uppfært' });
    },
    onError: () => {
      toast({ title: 'Villa', description: 'Ekki tókst að uppfæra sniðmát', variant: 'destructive' });
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: deleteTemplate,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['templates'] });
      setDeleteConfirm(null);
      toast({ title: 'Sniðmáti eytt' });
    },
    onError: () => {
      toast({ title: 'Villa', description: 'Ekki tókst að eyða sniðmáti', variant: 'destructive' });
    },
  });

  const copyOrgMutation = useMutation({
    mutationFn: copyOrgTemplate,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['templates'] });
      setCopyTemplate(null);
      toast({ title: 'Sniðmát afritað' });
    },
    onError: () => {
      toast({ title: 'Villa', description: 'Ekki tókst að afrita sniðmát', variant: 'destructive' });
    },
  });

  // Filter templates by search
  const filteredTemplates = templates.filter(
    (t) =>
      t.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      t.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Categorize templates
  const inUseIds = new Set([defaultDictationTemplateId, defaultConversationTemplateId].filter(Boolean));
  const personal = filteredTemplates.filter(isPersonalTemplate);
  const orgTemplates = filteredTemplates.filter((t) => t.scope === 'org' || t.is_org);
  const categorized: CategorizedTemplates = {
    inUse: personal.filter((t) => inUseIds.has(t.id)),
    myTemplates: personal.filter((t) => !inUseIds.has(t.id)),
    orgTemplates,
  };

  // Duplicate template
  const handleDuplicate = async (template: Template) => {
    try {
      const fullTemplate = await fetchTemplate(template.id);
      createMutation.mutate({
        name: `${fullTemplate.name} (afrit)`,
        description: fullTemplate.description,
        template_content: fullTemplate.template_content,
        example_note: fullTemplate.example_note,
        custom_prompt: fullTemplate.custom_prompt,
        is_default: false,
      });
    } catch {
      toast({ title: 'Villa', description: 'Ekki tókst að afrita sniðmát', variant: 'destructive' });
    }
  };

  // Get usage types for template (can be both dictation and conversation)
  const getUsageTypes = (templateId: number): Array<'dictation' | 'conversation'> => {
    const types: Array<'dictation' | 'conversation'> = [];
    if (defaultDictationTemplateId === templateId) types.push('dictation');
    if (defaultConversationTemplateId === templateId) types.push('conversation');
    return types;
  };

  return (
    <div className="flex h-screen bg-background">
      <Sidebar />
      <div className="flex flex-1 flex-col overflow-hidden">
        <Header title="Sniðmát" />
        <main className="flex-1 overflow-auto p-6">
          <div className="mx-auto max-w-5xl space-y-6">
            <TemplateScopeTabs active="personal" />

            {/* Header actions */}
            <div className="flex items-center justify-between gap-4">
              <div className="relative max-w-sm flex-1">
                <Search className="-translate-y-1/2 absolute top-1/2 left-3 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Leita að sniðmátum..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9"
                />
              </div>
              <div className="flex items-center gap-2">
                <Button onClick={() => setIsCreateOpen(true)}>
                  <Plus className="mr-2 h-4 w-4" />
                  Nýtt sniðmát
                </Button>
              </div>
            </div>

            {/* Templates accordion */}
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            ) : filteredTemplates.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <FileText className="mb-4 h-12 w-12 text-muted-foreground/50" />
                <h3 className="mb-1 font-medium text-lg">Engin sniðmát fundust</h3>
                <p className="mb-4 text-muted-foreground text-sm">
                  {searchQuery ? 'Prófaðu að breyta leitarorðum' : 'Búðu til þitt fyrsta sniðmát til að byrja'}
                </p>
                {!searchQuery && (
                  <Button onClick={() => setIsCreateOpen(true)}>
                    <Plus className="mr-2 h-4 w-4" />
                    Búa til sniðmát
                  </Button>
                )}
              </div>
            ) : (
              <Accordion
                type="multiple"
                value={expandedSections}
                onValueChange={setExpandedSections}
                className="space-y-3"
              >
                {/* In Use Section */}
                <AccordionItem value="in-use" className="rounded-lg border bg-card px-4 shadow-sm">
                  <AccordionTrigger className="hover:no-underline">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">Í notkun</span>
                      <Badge variant="secondary" className="h-5 px-1.5 text-xs">
                        {categorized.inUse.length}
                      </Badge>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent>
                    {categorized.inUse.length === 0 ? (
                      <p className="py-4 text-center text-muted-foreground text-sm">
                        Engin sniðmát í notkun. Veldu sniðmát úr þínum sniðmátum til að nota.
                      </p>
                    ) : (
                      <div className="space-y-1 pb-2">
                        {categorized.inUse.map((template) => (
                          <TemplateRow
                            key={template.id}
                            template={template}
                            onClick={() => handleTemplateClick(template)}
                            usageTypes={getUsageTypes(template.id)}
                            actions={
                              <InUseTemplateActions
                                usageTypes={getUsageTypes(template.id)}
                                onEdit={() => handleTemplateClick(template)}
                                onAddDictation={() => setDefaultDictation(template.id)}
                                onAddConversation={() => setDefaultConversation(template.id)}
                                onRemoveDictation={() => setDefaultDictation(null)}
                                onRemoveConversation={() => setDefaultConversation(null)}
                                onDuplicate={() => handleDuplicate(template)}
                              />
                            }
                          />
                        ))}
                      </div>
                    )}
                  </AccordionContent>
                </AccordionItem>

                {/* My Templates Section */}
                <AccordionItem value="my-templates" className="rounded-lg border bg-card px-4 shadow-sm">
                  <AccordionTrigger className="hover:no-underline">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">Þín sniðmát</span>
                      <Badge variant="secondary" className="h-5 px-1.5 text-xs">
                        {categorized.myTemplates.length}
                      </Badge>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent>
                    {categorized.myTemplates.length === 0 ? (
                      <p className="py-4 text-center text-muted-foreground text-sm">
                        Þú átt engin sniðmát ennþá. Búðu til nýtt eða afritaðu kerfissniðmát.
                      </p>
                    ) : (
                      <div className="space-y-1 pb-2">
                        {categorized.myTemplates.map((template) => (
                          <TemplateRow
                            key={template.id}
                            template={template}
                            onClick={() => handleTemplateClick(template)}
                            actions={
                              <PersonalTemplateActions
                                template={template}
                                defaultDictationTemplateId={defaultDictationTemplateId}
                                defaultConversationTemplateId={defaultConversationTemplateId}
                                onEdit={() => handleTemplateClick(template)}
                                onDuplicate={() => handleDuplicate(template)}
                                onSetDefaultDictation={() => setDefaultDictation(template.id)}
                                onSetDefaultConversation={() => setDefaultConversation(template.id)}
                                onDelete={() => setDeleteConfirm(template)}
                              />
                            }
                          />
                        ))}
                      </div>
                    )}
                  </AccordionContent>
                </AccordionItem>

                {/* Org Templates Section */}
                <AccordionItem value="org-templates" className="rounded-lg border bg-card px-4 shadow-sm">
                  <AccordionTrigger className="hover:no-underline">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">Stofnunarsniðmát</span>
                      <Badge variant="secondary" className="h-5 px-1.5 text-xs">
                        {categorized.orgTemplates.length}
                      </Badge>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent>
                    {categorized.orgTemplates.length === 0 ? (
                      <p className="py-4 text-center text-muted-foreground text-sm">Engin stofnunarsniðmát fundust.</p>
                    ) : (
                      <div className="space-y-1 pb-2">
                        <p className="mb-3 text-muted-foreground text-xs">
                          Stofnunarsniðmát eru aðeins til skoðunar. Afritaðu í þín sniðmát til að breyta.
                        </p>
                        {categorized.orgTemplates.map((template) => (
                          <TemplateRow
                            key={template.id}
                            template={template}
                            onClick={() => handleOrgTemplatePreview(template)}
                            actions={
                              <OrgTemplateActions
                                onView={() => handleOrgTemplatePreview(template)}
                                onCopy={() => copyOrgMutation.mutate(template.id)}
                                isCopying={copyOrgMutation.isPending}
                              />
                            }
                          />
                        ))}
                      </div>
                    )}
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            )}
          </div>
        </main>
      </div>

      {/* Create/Edit Sheet */}
      <Sheet
        open={isCreateOpen || !!editingTemplate || isLoadingTemplate}
        onOpenChange={(open) => {
          if (!open && !isLoadingTemplate) {
            setIsCreateOpen(false);
            setEditingTemplate(null);
          }
        }}
      >
        <SheetContent side="center" className="max-w-3xl overflow-y-auto">
          {isLoadingTemplate ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <>
              <SheetHeader>
                <SheetTitle>{editingTemplate ? 'Breyta sniðmáti' : 'Nýtt sniðmát'}</SheetTitle>
                <SheetDescription>
                  {editingTemplate
                    ? 'Breyttu sniðmátinu og vistaðu breytingar'
                    : 'Búðu til nýtt sniðmát fyrir klínískar samantektir'}
                </SheetDescription>
              </SheetHeader>
              <TemplateForm
                template={editingTemplate}
                onSubmit={(data) => {
                  if (editingTemplate) {
                    updateMutation.mutate({ id: editingTemplate.id, data });
                  } else {
                    createMutation.mutate(data);
                  }
                }}
                isLoading={createMutation.isPending || updateMutation.isPending}
              />
            </>
          )}
        </SheetContent>
      </Sheet>

      {/* Org copy preview sheet */}
      <Sheet
        open={!!copyTemplate || isLoadingCopyTemplate}
        onOpenChange={(open) => {
          if (!open && !isLoadingCopyTemplate) {
            setCopyTemplate(null);
          }
        }}
      >
        <SheetContent side="center" className="max-w-3xl overflow-y-auto">
          {isLoadingCopyTemplate ? (
            <>
              <SheetHeader>
                <SheetTitle>Sæki sniðmát</SheetTitle>
                <SheetDescription>Augnablik.</SheetDescription>
              </SheetHeader>
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            </>
          ) : (
            <>
              <SheetHeader>
                <SheetTitle>{copyTemplate?.name}</SheetTitle>
                <SheetDescription>
                  Stofnunarsniðmát. Afritaðu það í þín sniðmát til að breyta eða nota sem þitt eigið.
                </SheetDescription>
              </SheetHeader>
              {copyTemplate && (
                <div className="mt-6 space-y-6">
                  {copyTemplate.description && (
                    <div className="space-y-2">
                      <Label>Lýsing</Label>
                      <p className="rounded-md border bg-muted/40 p-3 text-sm">{copyTemplate.description}</p>
                    </div>
                  )}
                  <div className="space-y-2">
                    <Label>Sniðmát</Label>
                    <Textarea
                      value={copyTemplate.template_content}
                      readOnly
                      className="min-h-[200px] font-mono text-sm"
                    />
                  </div>
                  {copyTemplate.example_note && (
                    <div className="space-y-2">
                      <Label>Dæmi</Label>
                      <Textarea
                        value={copyTemplate.example_note}
                        readOnly
                        className="min-h-[150px] font-mono text-sm"
                      />
                    </div>
                  )}
                  <div className="flex justify-end border-t pt-4">
                    <Button
                      onClick={() => copyOrgMutation.mutate(copyTemplate.id)}
                      disabled={copyOrgMutation.isPending}
                    >
                      {copyOrgMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                      Afrita til mín
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </SheetContent>
      </Sheet>

      {/* Delete confirmation dialog */}
      <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Eyða sniðmáti?</DialogTitle>
            <DialogDescription>
              Ertu viss um að þú viljir eyða "{deleteConfirm?.name}"? Þetta er ekki hægt að afturkalla.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirm(null)}>
              Hætta við
            </Button>
            <Button
              variant="destructive"
              onClick={() => deleteConfirm && deleteMutation.mutate(deleteConfirm.id)}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Eyða
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Template row component for display in accordion sections
const TemplateRow = ({
  template,
  onClick,
  usageTypes = [],
  actions,
}: {
  template: Template;
  onClick: () => void;
  usageTypes?: Array<'dictation' | 'conversation'>;
  actions: React.ReactNode;
}) => (
  <div className="flex w-full items-center justify-between rounded-lg p-3 hover:bg-muted/50">
    <button
      type="button"
      className="flex min-w-0 flex-1 cursor-pointer items-center justify-between text-left"
      onClick={onClick}
    >
      <span className="flex min-w-0 items-center gap-3">
        <FileText className="h-4 w-4 shrink-0 text-muted-foreground" />
        <span className="min-w-0">
          <span className="block truncate font-medium">{template.name}</span>
          {template.description && (
            <span className="block truncate text-muted-foreground text-sm">{template.description}</span>
          )}
        </span>
      </span>
      <span className="ml-3 flex shrink-0 items-center gap-2">
        {usageTypes.includes('dictation') && (
          <Badge variant="outline" className="gap-1 border-blue-300 bg-white text-blue-600">
            <Mic className="h-3 w-3" /> Diktering
          </Badge>
        )}
        {usageTypes.includes('conversation') && (
          <Badge variant="outline" className="gap-1 border-emerald-300 bg-white text-emerald-600">
            <MessageSquare className="h-3 w-3" /> Samtal
          </Badge>
        )}
      </span>
    </button>
    <span className="ml-2 flex shrink-0 items-center gap-2">{actions}</span>
  </div>
);

// Actions for templates in the "In Use" section
const InUseTemplateActions = ({
  usageTypes,
  onEdit,
  onAddDictation,
  onAddConversation,
  onRemoveDictation,
  onRemoveConversation,
  onDuplicate,
}: {
  usageTypes: Array<'dictation' | 'conversation'>;
  onEdit: () => void;
  onAddDictation: () => void;
  onAddConversation: () => void;
  onRemoveDictation: () => void;
  onRemoveConversation: () => void;
  onDuplicate: () => void;
}) => (
  <DropdownMenu>
    <DropdownMenuTrigger asChild>
      <Button variant="ghost" size="icon" className="h-8 w-8">
        <MoreHorizontal className="h-4 w-4" />
      </Button>
    </DropdownMenuTrigger>
    <DropdownMenuContent align="end">
      <DropdownMenuItem onClick={onEdit}>
        <Pencil className="mr-2 h-4 w-4" />
        Breyta
      </DropdownMenuItem>
      <DropdownMenuItem onClick={onDuplicate}>
        <Copy className="mr-2 h-4 w-4" />
        Afrita
      </DropdownMenuItem>
      <DropdownMenuSeparator />
      {!usageTypes.includes('dictation') && (
        <DropdownMenuItem onClick={onAddDictation}>
          <Mic className="mr-2 h-4 w-4" />
          Nota einnig fyrir dikteringu
        </DropdownMenuItem>
      )}
      {!usageTypes.includes('conversation') && (
        <DropdownMenuItem onClick={onAddConversation}>
          <MessageSquare className="mr-2 h-4 w-4" />
          Nota einnig fyrir samtal
        </DropdownMenuItem>
      )}
      {usageTypes.includes('dictation') && (
        <DropdownMenuItem onClick={onRemoveDictation}>
          <X className="mr-2 h-4 w-4" />
          Fjarlægja úr dikteringu
        </DropdownMenuItem>
      )}
      {usageTypes.includes('conversation') && (
        <DropdownMenuItem onClick={onRemoveConversation}>
          <X className="mr-2 h-4 w-4" />
          Fjarlægja úr samtali
        </DropdownMenuItem>
      )}
    </DropdownMenuContent>
  </DropdownMenu>
);

// Actions for personal templates
const PersonalTemplateActions = ({
  template,
  defaultDictationTemplateId,
  defaultConversationTemplateId,
  onEdit,
  onDuplicate,
  onSetDefaultDictation,
  onSetDefaultConversation,
  onDelete,
}: {
  template: Template;
  defaultDictationTemplateId: number | null;
  defaultConversationTemplateId: number | null;
  onEdit: () => void;
  onDuplicate: () => void;
  onSetDefaultDictation: () => void;
  onSetDefaultConversation: () => void;
  onDelete: () => void;
}) => (
  <DropdownMenu>
    <DropdownMenuTrigger asChild>
      <Button variant="ghost" size="icon" className="h-8 w-8">
        <MoreHorizontal className="h-4 w-4" />
      </Button>
    </DropdownMenuTrigger>
    <DropdownMenuContent align="end">
      <DropdownMenuItem onClick={onEdit}>
        <Pencil className="mr-2 h-4 w-4" />
        Breyta
      </DropdownMenuItem>
      <DropdownMenuItem onClick={onDuplicate}>
        <Copy className="mr-2 h-4 w-4" />
        Afrita
      </DropdownMenuItem>
      <DropdownMenuSeparator />
      {defaultDictationTemplateId !== template.id && (
        <DropdownMenuItem onClick={onSetDefaultDictation}>
          <Mic className="mr-2 h-4 w-4" />
          Nota fyrir dikteringu
        </DropdownMenuItem>
      )}
      {defaultConversationTemplateId !== template.id && (
        <DropdownMenuItem onClick={onSetDefaultConversation}>
          <MessageSquare className="mr-2 h-4 w-4" />
          Nota fyrir samtal
        </DropdownMenuItem>
      )}
      <DropdownMenuSeparator />
      <DropdownMenuItem className="text-destructive focus:text-destructive" onClick={onDelete}>
        <Trash2 className="mr-2 h-4 w-4" />
        Eyða
      </DropdownMenuItem>
    </DropdownMenuContent>
  </DropdownMenu>
);

const OrgTemplateActions = ({
  onView,
  onCopy,
  isCopying,
}: {
  onView: () => void;
  onCopy: () => void;
  isCopying: boolean;
}) => (
  <DropdownMenu>
    <DropdownMenuTrigger asChild>
      <Button variant="ghost" size="icon" className="h-8 w-8">
        <MoreHorizontal className="h-4 w-4" />
      </Button>
    </DropdownMenuTrigger>
    <DropdownMenuContent align="end">
      <DropdownMenuItem onClick={onView}>
        <Eye className="mr-2 h-4 w-4" />
        Skoða
      </DropdownMenuItem>
      <DropdownMenuItem onClick={onCopy} disabled={isCopying}>
        {isCopying ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Copy className="mr-2 h-4 w-4" />}
        Afrita til mín
      </DropdownMenuItem>
    </DropdownMenuContent>
  </DropdownMenu>
);

// Template form component
function TemplateForm({
  template,
  onSubmit,
  isLoading,
}: {
  template: Template | null;
  onSubmit: (data: Partial<Template>) => void;
  isLoading: boolean;
}) {
  const [name, setName] = useState(template?.name || '');
  const [description, setDescription] = useState(template?.description || '');
  const [templateContent, setTemplateContent] = useState(template?.template_content || '');
  const [exampleNote, setExampleNote] = useState(template?.example_note || '');
  const [customPrompt, setCustomPrompt] = useState(template?.custom_prompt || '');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit({
      name,
      description: description || null,
      template_content: templateContent,
      example_note: exampleNote,
      custom_prompt: customPrompt || null,
    });
  };

  return (
    <form onSubmit={handleSubmit} className="mt-6 space-y-6">
      <div className="space-y-2">
        <Label htmlFor="name">Nafn *</Label>
        <Input
          id="name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="t.d. Almenn læknisskoðun"
          required
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="description">Lýsing</Label>
        <Input
          id="description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Stutt lýsing á sniðmátinu"
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="template_content">Sniðmát *</Label>
        <Textarea
          id="template_content"
          value={templateContent}
          onChange={(e) => setTemplateContent(e.target.value)}
          placeholder="Uppbygging nótunnar, t.d.:&#10;&#10;Saga:&#10;[Lýsing á einkennum]&#10;&#10;Skoðun:&#10;[Niðurstöður skoðunar]&#10;&#10;Á/P:&#10;[Greining og áætlun]"
          className="min-h-[200px] font-mono text-sm"
          required
        />
        <p className="text-muted-foreground text-xs">Notaðu [sviga] til að merkja svæði sem á að fylla út</p>
      </div>

      <div className="space-y-2">
        <Label htmlFor="example_note">Dæmi (valfrjálst)</Label>
        <Textarea
          id="example_note"
          value={exampleNote}
          onChange={(e) => setExampleNote(e.target.value)}
          placeholder="Dæmi um útfyllta nótu..."
          className="min-h-[150px] font-mono text-sm"
        />
        <p className="text-muted-foreground text-xs">
          Dæmi hjálpar gervigreindinni að skilja hvernig á að fylla út sniðmátið
        </p>
      </div>

      <div className="space-y-2">
        <Label htmlFor="custom_prompt">Sérsniðin fyrirmæli (valfrjálst)</Label>
        <Textarea
          id="custom_prompt"
          value={customPrompt}
          onChange={(e) => setCustomPrompt(e.target.value)}
          placeholder="Viðbótar leiðbeiningar fyrir gervigreindina..."
          className="min-h-[100px]"
        />
      </div>

      <div className="flex justify-end gap-3 border-t pt-4">
        <Button type="submit" disabled={isLoading || !name || !templateContent}>
          {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          {template ? 'Vista breytingar' : 'Búa til sniðmát'}
        </Button>
      </div>
    </form>
  );
}
